import React, { useEffect, useState } from "react";
import axios from "axios";
import { APPT_STATUS_LABEL, label } from "@/lib/labels";
import { Link } from "react-router-dom";
import { useApp, API } from "@/contexts/AppContext";
import { SPECIALTIES, SERVICE_LABEL } from "@/lib/labels";
import { toast } from "sonner";
import {
  Sunrise, Calendar, BedDouble, FileText, Users, ArrowRight,
  Clock, Stethoscope, MessageSquare, Sparkles,
} from "lucide-react";

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: "Africa/Kinshasa" }); }
  catch { return "—"; }
};
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
};
const fmtRelative = (iso) => {
  try {
    const d = new Date(iso);
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diffMin < 60) return `Il y a ${diffMin} min`;
    if (diffMin < 1440) return `Il y a ${Math.floor(diffMin / 60)} h`;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
  } catch { return "—"; }
};

const KPI = ({ label, value, icon: Icon, color, testid }) => (
  <div className="bg-white rounded-2xl border border-[#E5EBE7] p-5" data-testid={testid}>
    <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: `${color}15`, color }}>
      <Icon className="w-5 h-5"/>
    </div>
    <div className="mt-3 text-3xl font-[Outfit] font-bold text-[#1A1D1A]">{value}</div>
    <div className="text-xs text-[#5C6B61] mt-0.5">{label}</div>
  </div>
);

export default function MyDay() {
  const { user, setSession, token } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const saveSpecialty = async (specialty) => {
    try {
      const r = await axios.patch(`${API}/users/me/profile`, { specialty });
      setSession(token, { ...user, specialty: r.data.specialty, service: r.data.service });
      toast.success(`Spécialité enregistrée : ${specialty}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  useEffect(() => {
    axios.get(`${API}/dashboard/my-day`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-[#5C6B61]">Chargement de votre journée…</div>;
  if (!data) return <div className="text-[#5C6B61]">Impossible de charger.</div>;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Bonjour" : hour < 18 ? "Bon après-midi" : "Bonsoir";
  const firstName = (user?.name || "").split(" ").slice(-1)[0] || (user?.name || "");

  return (
    <div className="space-y-6" data-testid="myday-page">
      {/* Hero greeting */}
      <div className="bg-gradient-to-br from-[#1E4631] to-[#2E7D32] text-white rounded-2xl p-7 relative overflow-hidden">
        <div className="absolute -top-8 -right-8 w-40 h-40 bg-[#D96C4A]/30 rounded-full blur-3xl"/>
        <div className="absolute -bottom-10 -left-8 w-48 h-48 bg-[#E3A72F]/20 rounded-full blur-3xl"/>
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#E3A72F] font-semibold">
            <Sunrise className="w-4 h-4"/> Ma journée · {fmtDate(data.today.date_iso)}
          </div>
          <h1 className="mt-2 text-4xl font-[Outfit] font-bold" data-testid="myday-greeting">
            {greeting}, Dr {firstName}
          </h1>
          <p className="mt-2 text-white/80 text-sm max-w-xl">
            Voici un aperçu personnalisé de vos patients, rendez-vous et notes. Bonne journée !
          </p>
          {user?.role === "doctor" && (
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm" data-testid="doctor-specialty-block">
              <Stethoscope className="w-4 h-4 text-[#E3A72F]"/>
              <span className="text-white/80">Spécialité :</span>
              <select value={user?.specialty || ""} onChange={(e) => saveSpecialty(e.target.value)} data-testid="doctor-specialty-select"
                className="bg-white/10 border border-white/25 rounded-lg px-3 py-1.5 text-white text-sm outline-none focus:border-[#E3A72F] [&>option]:text-[#1A1D1A]">
                <option value="">— Choisir ma spécialité —</option>
                {SPECIALTIES.map((sp) => <option key={sp} value={sp}>{sp}</option>)}
              </select>
              {user?.service && user.service !== "general" && (
                <span className="px-2 py-0.5 rounded-full bg-white/15 text-xs">{SERVICE_LABEL[user.service]}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label="RDV aujourd'hui" value={data.today.appointments_count} icon={Calendar} color="#D96C4A" testid="kpi-today-appts"/>
        <KPI label="Mes patients (total)" value={data.stats.my_patients_total} icon={Users} color="#1E4631" testid="kpi-my-patients"/>
        <KPI label="Hospitalisés" value={data.stats.hospitalized_count} icon={BedDouble} color="#2E7D32" testid="kpi-hospitalized"/>
        <KPI label="Notes cette semaine" value={data.stats.notes_last_7_days} icon={FileText} color="#E3A72F" testid="kpi-notes"/>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Today's appointments */}
        <div className="bg-white rounded-2xl border border-[#E5EBE7] p-6" data-testid="today-appts-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-[Outfit] font-semibold text-lg inline-flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#D96C4A]"/> RDV d&apos;aujourd&apos;hui
            </h2>
            <Link to="/dashboard/appointments" className="text-xs text-[#5C6B61] hover:text-[#1E4631] inline-flex items-center gap-1" data-testid="see-all-appts">
              Voir tout <ArrowRight className="w-3 h-3"/>
            </Link>
          </div>
          {data.today.appointments.length === 0 ? (
            <div className="text-center py-8 text-sm text-[#5C6B61]">
              <Sparkles className="w-10 h-10 mx-auto text-[#E3A72F] mb-2 opacity-40"/>
              <div>Aucun rendez-vous aujourd&apos;hui.</div>
              <div className="text-xs mt-1">Profitez-en pour vérifier vos hospitalisés ou revoir vos notes.</div>
            </div>
          ) : (
            <ul className="divide-y divide-[#E5EBE7]">
              {data.today.appointments.map((a) => (
                <li key={a.id} className="py-3 flex items-start gap-3">
                  <div className="w-14 flex-shrink-0 text-center">
                    <div className="text-lg font-[Outfit] font-bold text-[#1E4631]">{fmtTime(a.date)}</div>
                    <div className="text-[10px] uppercase tracking-wide text-[#5C6B61]">
                      <Clock className="w-3 h-3 inline"/>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-[#1A1D1A] truncate">{a.patient_name}</div>
                    <div className="text-xs text-[#5C6B61] truncate">{a.reason}</div>
                    <div className="text-xs text-[#5C6B61] mt-0.5">📞 {a.patient_phone}</div>
                  </div>
                  <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded ${
                    a.status === "scheduled" ? "bg-[#2E7D32]/10 text-[#2E7D32]" : "bg-[#5C6B61]/10 text-[#5C6B61]"
                  }`}>
                    {label(APPT_STATUS_LABEL, a.status)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Hospitalized patients */}
        <div className="bg-white rounded-2xl border border-[#E5EBE7] p-6" data-testid="hospitalized-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-[Outfit] font-semibold text-lg inline-flex items-center gap-2">
              <BedDouble className="w-5 h-5 text-[#2E7D32]"/> Mes patients hospitalisés
            </h2>
            <Link to="/dashboard/hospitalizations" className="text-xs text-[#5C6B61] hover:text-[#1E4631] inline-flex items-center gap-1" data-testid="see-all-hospitalized">
              Voir tout <ArrowRight className="w-3 h-3"/>
            </Link>
          </div>
          {data.hospitalized_patients.length === 0 ? (
            <div className="text-center py-8 text-sm text-[#5C6B61]">
              <BedDouble className="w-10 h-10 mx-auto text-[#2E7D32] mb-2 opacity-40"/>
              <div>Aucun patient hospitalisé pour l&apos;instant.</div>
            </div>
          ) : (
            <ul className="divide-y divide-[#E5EBE7]">
              {data.hospitalized_patients.slice(0, 6).map((p) => (
                <li key={p.id} className="py-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#2E7D32]/10 grid place-items-center text-[#2E7D32]">
                    <Stethoscope className="w-5 h-5"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-[#1A1D1A] truncate">{p.name}</div>
                    <div className="text-xs text-[#5C6B61] truncate">
                      {p.hospitalization?.room_number && `Chambre ${p.hospitalization.room_number} · `}
                      {p.hospitalization?.admission_reason || "Sans motif renseigné"}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Recent notes */}
      <div className="bg-white rounded-2xl border border-[#E5EBE7] p-6" data-testid="recent-notes-card">
        <h2 className="font-[Outfit] font-semibold text-lg inline-flex items-center gap-2 mb-4">
          <MessageSquare className="w-5 h-5 text-[#E3A72F]"/> Mes notes récentes (7 derniers jours)
        </h2>
        {data.recent_notes.length === 0 ? (
          <div className="text-center py-8 text-sm text-[#5C6B61]">
            <FileText className="w-10 h-10 mx-auto text-[#E3A72F] mb-2 opacity-40"/>
            <div>Aucune note écrite cette semaine.</div>
            <div className="text-xs mt-1">Rendez-vous sur un dossier patient pour ajouter une note médicale.</div>
          </div>
        ) : (
          <ul className="space-y-3">
            {data.recent_notes.map((n) => (
              <li key={n.id} className="p-3 rounded-lg bg-[#FDFBF7] border border-[#E5EBE7]">
                <div className="flex items-center justify-between text-xs text-[#5C6B61] mb-1">
                  <span className="font-semibold text-[#1E4631]">{n.patient_name}</span>
                  <span>{fmtRelative(n.at)}</span>
                </div>
                <div className="text-sm text-[#1A1D1A] whitespace-pre-wrap">{n.content}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
